import { z } from "zod";

/**
 * Player actions are the "authority seam": every economic mutation is expressed as intent + context,
 * never as an amount. The server re-derives the amount from data. See docs/server-authority.md.
 */
export const PlayerActionSchema = z.object({
  /** Client-generated, unique per logical action. Replays with the same key are deduplicated. */
  idempotencyKey: z.string().min(8).max(128),
  /** Action type, e.g. "claim_reward". The set of types is defined by the engine's economy module. */
  type: z.string().min(1),
  /** Stable reference for the thing being acted on, e.g. a reward id from rewards.csv. */
  ref: z.string().min(1),
  /** Free-form context the server may use to re-derive the outcome. Never trusted for amounts. */
  context: z.record(z.string(), z.unknown()).default({}),
  clientTime: z.string().datetime(),
});
export type PlayerAction = z.infer<typeof PlayerActionSchema>;

/**
 * `pending`: the server cannot apply the action yet (an ad reward whose server-side verification
 * has not arrived). Nothing was granted; the client keeps the action queued and retries with backoff.
 */
export const PlayerActionStatusSchema = z.enum(["applied", "duplicate", "rejected", "pending"]);
export type PlayerActionStatus = z.infer<typeof PlayerActionStatusSchema>;

export const PlayerActionResultSchema = z.object({
  status: PlayerActionStatusSchema,
  reason: z.string().optional(),
  /** Authoritative wallet after the action. */
  wallet: z.record(z.string(), z.number().int().nonnegative()),
  /** Save revision after the action (actions bump the revision). */
  revision: z.number().int().nonnegative(),
});
export type PlayerActionResult = z.infer<typeof PlayerActionResultSchema>;
